import React, { useState, useEffect, useRef } from "react";
import { View, Text, TouchableOpacity, StyleSheet, Animated } from "react-native";
import { Ionicons } from "@expo/vector-icons";

const NotificationItem = ({ message, type = "error", onDismiss, timestamp, project }) => {
	const [opacity] = useState(new Animated.Value(1));

	useEffect(() => {
		// Only auto-dismiss success notifications
		if (type === "success") {
			const fadeOutTimer = setTimeout(() => {
				Animated.timing(opacity, {
					toValue: 0,
					duration: 500,
					useNativeDriver: false,
				}).start(() => onDismiss());
			}, 2500);

			return () => clearTimeout(fadeOutTimer);
		}
	}, [type]);

	const getIcon = () => {
		switch (type) {
			case "error":
				return "alert-circle";
			case "success":
				return "checkmark-circle";
			case "warning":
				return "warning";
			default:
				return "information-circle";
		}
	};

	const getColor = () => {
		switch (type) {
			case "error":
				return "#FF3B30";
			case "success":
				return "#34C759";
			case "warning":
				return "#FF9500";
			default:
				return "#0A84FF";
		}
	};

	return (
		<Animated.View style={[styles.notificationItem, { borderLeftColor: getColor(), opacity }]}>
			<Ionicons name={getIcon()} size={20} color={getColor()} />
			<View style={styles.notificationContent}>
				<Text style={styles.notificationMessage}>
					{project && <Text style={styles.projectName}>{project.name}: </Text>}
					{message}
				</Text>
				<Text style={styles.notificationTime}>{timestamp}</Text>
			</View>
			<TouchableOpacity onPress={onDismiss} style={styles.dismissButton}>
				<Ionicons name="close" size={20} color="#666" />
			</TouchableOpacity>
		</Animated.View>
	);
};

const Notification = ({ notifications = [], onDismiss }) => {
	const [showDropdown, setShowDropdown] = useState(false);
	const [notificationType, setNotificationType] = useState(null);
	const badgeOpacity = useRef(new Animated.Value(1)).current;

	useEffect(() => {
		// Determine notification type for badge color
		if (notifications.length > 0) {
			const hasError = notifications.some((n) => n.type === "error");
			const hasSuccess = notifications.some((n) => n.type === "success");
			setNotificationType(hasError ? "error" : hasSuccess ? "success" : null);

			// Start flashing animation
			Animated.sequence([
				Animated.timing(badgeOpacity, {
					toValue: 0.2,
					duration: 500,
					useNativeDriver: false,
				}),
				Animated.timing(badgeOpacity, {
					toValue: 1,
					duration: 500,
					useNativeDriver: false,
				}),
			]).start(() => {
				// After 3 seconds, fade out the badge
				setTimeout(() => {
					Animated.timing(badgeOpacity, {
						toValue: 0,
						duration: 500,
						useNativeDriver: false,
					}).start();
				}, 2000);
			});
		} else {
			setNotificationType(null);
		}
	}, [notifications]);

	const getBadgeColor = () => {
		switch (notificationType) {
			case "error":
				return "#FF3B30";
			case "success":
				return "#34C759";
			default:
				return "#FF3B30";
		}
	};

	return (
		<View style={styles.container}>
			<TouchableOpacity style={styles.notificationTrigger} onPress={() => setShowDropdown(!showDropdown)}>
				<Ionicons name="notifications-outline" size={20} color="#888" />
				{notifications.length > 0 && <Animated.View style={[styles.badge, { backgroundColor: getBadgeColor(), opacity: badgeOpacity }]} />}
			</TouchableOpacity>

			{showDropdown && (
				<View style={styles.dropdown}>
					<View style={styles.dropdownHeader}>
						<Text style={styles.dropdownTitle}>Notifications</Text>
						{notifications.length > 0 && (
							<TouchableOpacity onPress={() => onDismiss("all")}>
								<Text style={styles.clearAll}>Clear all</Text>
							</TouchableOpacity>
						)}
					</View>
					<View style={styles.notificationsList}>
						{notifications.length === 0 ? <Text style={styles.emptyText}>No notifications</Text> : notifications.map((notification) => <NotificationItem key={notification.id} {...notification} onDismiss={() => onDismiss(notification.id)} />)}
					</View>
				</View>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		position: "relative",
	},
	notificationTrigger: {
		padding: 8,
		justifyContent: "center",
		alignItems: "center",
		position: "relative",
	},
	badge: {
		position: "absolute",
		top: 6,
		right: 6,
		width: 8,
		height: 8,
		borderRadius: 4,
		backgroundColor: "#FF3B30",
	},
	dropdown: {
		position: "absolute",
		top: 48,
		right: 0,
		width: 320,
		backgroundColor: "#1A1A1A",
		borderRadius: 12,
		borderWidth: 1,
		borderColor: "#2A2A2A",
		shadowColor: "#000",
		shadowOffset: { width: 0, height: 4 },
		shadowOpacity: 0.25,
		shadowRadius: 8,
		zIndex: 1000,
	},
	dropdownHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		padding: 16,
		borderBottomWidth: 1,
		borderBottomColor: "#2A2A2A",
	},
	dropdownTitle: {
		fontSize: 16,
		fontWeight: "600",
		color: "#fff",
	},
	clearAll: {
		fontSize: 14,
		color: "#0A84FF",
	},
	notificationsList: {
		maxHeight: 400,
	},
	notificationItem: {
		flexDirection: "row",
		alignItems: "center",
		padding: 12,
		borderLeftWidth: 4,
		gap: 12,
	},
	notificationContent: {
		flex: 1,
		marginRight: 8,
	},
	notificationMessage: {
		fontSize: 14,
		color: "#fff",
		marginBottom: 4,
		flexDirection: "row",
		flexWrap: "wrap",
	},
	notificationTime: {
		fontSize: 12,
		color: "#666",
	},
	dismissButton: {
		padding: 4,
	},
	emptyText: {
		color: "#666",
		fontSize: 14,
		textAlign: "center",
		padding: 16,
	},
	projectName: {
		fontSize: 14,
		fontWeight: "600",
		color: "#fff",
	},
});

export default Notification;
